import { MGPMap, MGPOptional, ReversibleMap, Utils } from '@everyboard/lib';
import { Coord } from '../../jscaip/Coord';
import { HexagonalUtils } from '../../jscaip/HexagonalUtils';
import { Player } from '../../jscaip/Player';
import { Vector } from '../../jscaip/Vector';
import { OpenHexagonalGameState } from '../../jscaip/state/OpenHexagonalGameState';
import { HivePiece, HivePieceStack } from './HivePiece';
export class HiveRemainingPieces {
    pieces;
    static getInitial() {
        const pieces = new MGPMap();
        for (const player of Player.PLAYERS) {
            pieces.set(new HivePiece(player, 'QueenBee'), 1);
            pieces.set(new HivePiece(player, 'Beetle'), 2);
            pieces.set(new HivePiece(player, 'Spider'), 2);
            pieces.set(new HivePiece(player, 'Grasshopper'), 3);
            pieces.set(new HivePiece(player, 'SoldierAnt'), 3);
        }
        pieces.makeImmutable();
        return new HiveRemainingPieces(pieces);
    }
    constructor(pieces) {
        this.pieces = pieces;
    }
    equals(other) {
        return this.pieces.equals(other.pieces);
    }
    getQuantity(piece) {
        return this.pieces.get(piece).get();
    }
    hasRemaining(piece) {
        return this.getQuantity(piece) > 0;
    }
    getAny(player) {
        for (const piece of this.pieces.getKeyList()) {
            if (piece.owner === player && this.hasRemaining(piece)) {
                return MGPOptional.of(piece);
            }
        }
        return MGPOptional.empty();
    }
    remove(piece) {
        const remaining = this.pieces.get(piece).get();
        Utils.assert(remaining > 0, 'HiveRemainingPieces cannot remove a non-remainingPiece');
        const newPieces = this.pieces.getCopy();
        newPieces.replace(piece, remaining - 1);
        return new HiveRemainingPieces(newPieces);
    }
    toListOfStacks() {
        const remaining = [];
        for (const [piece, value] of this.pieces) {
            const pieces = [];
            for (let i = 0; i < value; i++) {
                pieces.push(piece);
            }
            remaining.push(new HivePieceStack(pieces));
        }
        return remaining;
    }
    getPlayerPieces(player) {
        const remaining = [];
        for (const [piece, value] of this.pieces) {
            if (piece.owner === player && value > 0) {
                remaining.push(piece);
            }
        }
        return remaining;
    }
}
/* Like HiveState, but does not recompute the offset between changes.
 * Can only be used while making temporary changes to the state.
 * Has to be converted to a regular HiveState after all changes have been made.
 */
class HiveStateUpdate {
    pieces;
    remainingPieces;
    queenBees;
    turn;
    static of(state) {
        return new HiveStateUpdate(state.pieces, state.remainingPieces, state.queenBees, state.turn);
    }
    constructor(pieces, remainingPieces, queenBees, turn) {
        this.pieces = pieces;
        this.remainingPieces = remainingPieces;
        this.queenBees = queenBees;
        this.turn = turn;
    }
    setAt(coord, stack) {
        const queenBees = this.queenBees.getCopy();
        for (const player of Player.PLAYERS) {
            // If there was a queen bee here, we remove it from the cache
            if (queenBees.get(player).equalsValue(coord)) {
                queenBees.delete(player);
            }
        }
        for (const piece of stack.pieces) {
            // Add any queen added here to the cache
            if (piece.kind === 'QueenBee') {
                queenBees.put(piece.owner, coord);
            }
        }
        const pieces = this.pieces.getCopy();
        if (stack.isEmpty()) {
            pieces.delete(coord);
        }
        else {
            pieces.put(coord, stack);
        }
        return new HiveStateUpdate(pieces, this.remainingPieces, queenBees, this.turn);
    }
    removeRemainingPiece(piece) {
        return new HiveStateUpdate(this.pieces, this.remainingPieces.remove(piece), this.queenBees, this.turn);
    }
    increaseTurnAndFinalizeUpdate() {
        return new HiveState(this.pieces, this.remainingPieces, this.queenBees, this.turn + 1);
    }
}
export class HiveState extends OpenHexagonalGameState {
    remainingPieces;
    queenBees;
    static fromRepresentation(board, turn, vector = new Vector(0, 0)) {
        const pieces = new ReversibleMap();
        let remainingPieces = HiveRemainingPieces.getInitial();
        const queenBees = new MGPMap();
        for (let y = 0; y < board.length; y++) {
            for (let x = 0; x < board[0].length; x++) {
                if (board[y][x].length > 0) {
                    const adaptedCoord = new Coord(x, y).getNext(vector, 1);
                    pieces.set(adaptedCoord, new HivePieceStack(board[y][x]));
                    const queenBee = MGPOptional.ofNullable(board[y][x].find((piece) => piece.kind === 'QueenBee'));
                    if (queenBee.isPresent()) {
                        queenBees.set(queenBee.get().owner, adaptedCoord);
                    }
                    for (const piece of board[y][x]) {
                        remainingPieces = remainingPieces.remove(piece);
                    }
                }
            }
        }
        return new HiveState(pieces, remainingPieces, queenBees, turn);
    }
    constructor(pieces, remainingPieces, queenBees, turn) {
        super(pieces, turn);
        this.remainingPieces = remainingPieces;
        this.queenBees = queenBees;
        this.queenBees = queenBees.getCopy();
        for (const player of queenBees.getKeyList()) {
            // If the offset computed by the parent's constructor is not (0, 0),
            // We will need to adapt the position of the queen bees.
            // The position of the pieces has already been adapted by the parent's constructor
            const oldCoord = queenBees.get(player).get();
            this.queenBees.replace(player, oldCoord);
        }
        this.queenBees.makeImmutable();
    }
    update() {
        return HiveStateUpdate.of(this);
    }
    equals(other) {
        return this.pieces.equals(other.pieces) &&
            this.remainingPieces.equals(other.remainingPieces) &&
            this.queenBees.equals(other.queenBees) &&
            this.turn === other.turn;
    }
    getAt(coord) {
        if (this.isOnBoard(coord)) {
            return this.pieces.get(coord).get();
        }
        else {
            return HivePieceStack.EMPTY;
        }
    }
    queenBeeLocation(player) {
        return this.queenBees.get(player);
    }
    hasQueenBeeOnBoard(player) {
        return this.queenBeeLocation(player).isPresent();
    }
    numberOfNeighbors(coord) {
        let neighbors = 0;
        for (const neighbor of HexagonalUtils.getNeighbors(coord)) {
            if (this.getAt(neighbor).hasPieces()) {
                neighbors += 1;
            }
        }
        return neighbors;
    }
    isDisconnected() {
        return this.getGroups().size() > 1;
    }
    occupiedSpaces() {
        return this.pieces.getKeyList();
    }
    emptyNeighbors(coord) {
        const result = [];
        for (const neighbor of HexagonalUtils.getNeighbors(coord)) {
            if (this.getAt(neighbor).isEmpty()) {
                result.push(neighbor);
            }
        }
        return result;
    }
    haveCommonNeighbor(first, second) {
        const occupiedNeighborsOfFirst = this.getOccupiedNeighbors(first);
        const occupiedNeighborsOfSecond = this.getOccupiedNeighbors(second);
        const commonNeighbor = occupiedNeighborsOfFirst.findAnyCommonElement(occupiedNeighborsOfSecond);
        return commonNeighbor.isPresent();
    }
}
